import React, { useEffect } from 'react';
import { X, ArrowLeft } from 'lucide-react';

/**
 * RightDrawer — slides in from the right (iOS-style bezier, 320ms).
 * Usage:
 * <RightDrawer open={isOpen} onClose={close} title="..." step="Step 2/2" onBack={...}>
 * {content}
 * </RightDrawer>
 */
export const RightDrawer = ({
 open,
 onClose,
 onBack,
 title,
 subtitle,
 step,
 children,
 footer,
 widthClass = 'max-w-[520px]',
 testId = 'right-drawer',
}) => {
 // Body scroll-lock is now handled globally by GlobalModalScrollLock based on
 // the (conditional) role="dialog" + aria-modal attributes below — no need
 // for a per-drawer manual lock here.

 useEffect(() => {
 const onKey = (e) => { if (e.key === 'Escape' && open) onClose?.(); };
 window.addEventListener('keydown', onKey);
 return () => window.removeEventListener('keydown', onKey);
 }, [open, onClose]);

 return (
 <>
 <div
 className={`gg-drawer-overlay ${open ? 'open' : 'pointer-events-none'}`}
 onClick={onClose}
 aria-hidden={!open}
 />
 <aside
 className={`gg-drawer-panel ${widthClass} ${open ? 'open' : ''}`}
 role={open ? 'dialog' : undefined}
 aria-modal={open ? 'true' : undefined}
 aria-hidden={!open}
 data-testid={testId}
 >
 <header className="flex items-center gap-3 px-6 pt-6 pb-4 border-b border-[var(--gg-hair)]">
 {onBack ? (
 <button
 onClick={onBack}
 className="w-9 h-9 -ml-2 flex items-center justify-center rounded-lg hover:bg-[var(--gg-bg)] text-[var(--gg-ink)]"
 data-testid={`${testId}-back`}
 >
 <ArrowLeft className="w-[18px] h-[18px]" />
 </button>
 ) : null}
 <div className="flex-1 min-w-0">
 {step && (
 <div className="text-[11px] uppercase tracking-[0.08em] text-[var(--gg-muted-2)] font-semibold mb-1">
 {step}
 </div>
 )}
 {title && <h2 className="text-[19px] font-semibold text-[var(--gg-ink)] tracking-tight truncate">{title}</h2>}
 {subtitle && <p className="text-[13px] text-[var(--gg-muted)] mt-0.5 truncate">{subtitle}</p>}
 </div>
 <button
 onClick={onClose}
 className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[var(--gg-bg)] text-[var(--gg-muted)]"
 data-testid={`${testId}-close`}
 aria-label="Close"
 >
 <X className="w-[18px] h-[18px]" />
 </button>
 </header>
 <div
   className="flex-1 overflow-y-auto overscroll-contain px-6 py-6"
   data-lenis-prevent
   data-lenis-prevent-wheel
   data-lenis-prevent-touch
   style={{ WebkitOverflowScrolling: 'touch' }}
 >{children}</div>
 {footer && (
 <footer className="px-6 py-4 border-t border-[var(--gg-hair)] bg-white">
 {footer}
 </footer>
 )}
 </aside>
 </>
 );
};

export default RightDrawer;
